import { useTranslation } from 'next-i18next'

import {
  PURCHASE_TYPES,
  PurchaseType,
} from '@src/quince-components/src/atoms/purchaseTypeSelector/PurchaseTypeSelector'
import Product from '@src/quince-site-common/src/domains/Product'
import type ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, {
  getEarnCreditTagFor3PVendor,
  getUnitForPerServing,
  shouldShowPricePerUnit,
} from '@src/quince-site-common/src/helpers/productHelper'
import { getPriceStyleKey } from '@src/quince-site-common/src/utils/festivalOffersUtility'

export const STYLE_KEY_MAP = {
  festival: 'festivalOffer' as const,
  subscription: 'subscriptionOffer' as const,
  default: 'default' as const,
}

export type OfferStyleKey = (typeof STYLE_KEY_MAP)[keyof typeof STYLE_KEY_MAP]

export const DIFFERENCE_TO_IGNORE = 0.1

export interface ProductInfoPriceDSProps {
  product?: Product
  formattedSalePrice: string
  formattedTraditionalPrice?: string
  savingPercentage?: number
  formattedFestivalOfferPrice?: string
  showDiscount?: boolean
  pricePerServing?: number
  traditionalPricePerServing?: number
  festivalPricePerUnit?: number
  subscriptionOfferPricePerUnit?: number
  hideSavings?: boolean
  isFinalSaleProduct?: boolean
  isB2BFinalSaleProduct?: boolean
  formattedSubscriptionPrice?: string
  purchaseOption?: PurchaseType
  updatedDesignForV2?: boolean
  isVariantThirdParty?: boolean
  selectedOptions?: ProductVariantOption[]
  /**
   * SVD ONLY — tags already scoped to the resolved variant, supplied by `PRODUCT_PURCHASE_V2`
   * (`use-svd-variant-tags`). When set it REPLACES the `getApplicableTags` scoping below, because the
   * SVD buy box cannot drive that scoping: it passes `selectedOptions: []`, which makes
   * `getApplicableTags` a pass-through (every tag is "applicable"), and it works in canonical VDA keys
   * rather than the legacy `Color`/display-value pairs those tags are keyed on.
   *
   * Undefined on EVERY legacy render — no non-SVD caller passes it — so the legacy tag path is untouched.
   */
  svdVariantTags?: ProductTag[]
  brandName?: string
}

// Derives the render flags for ProductInfoPriceDS; telemetry stays in the orchestrator.
export function usePriceDisplayState(props: ProductInfoPriceDSProps) {
  const {
    product,
    formattedSubscriptionPrice,
    formattedSalePrice,
    formattedTraditionalPrice,
    savingPercentage,
    formattedFestivalOfferPrice,
    showDiscount = true,
    pricePerServing,
    traditionalPricePerServing,
    hideSavings,
    purchaseOption,
    isVariantThirdParty,
    selectedOptions,
    svdVariantTags,
    brandName,
  } = props

  const { t } = useTranslation('common')
  const isDiscountLabelPresent =
    showDiscount && formattedTraditionalPrice
      ? Number(formattedTraditionalPrice) - Number(formattedSalePrice) > DIFFERENCE_TO_IGNORE
      : false
  const showPricePerUnit = shouldShowPricePerUnit(product)
  const hasPerUnitPricing = showPricePerUnit && !!pricePerServing && !!traditionalPricePerServing
  const perUnitSavingsPercentage = hasPerUnitPricing
    ? productHelper.getSavingPercentage(traditionalPricePerServing, pricePerServing)
    : ''

  const showBestMarketValue = isVariantThirdParty && !!brandName

  const showSavingPercentage = !!(
    !formattedFestivalOfferPrice &&
    !(formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE) &&
    formattedTraditionalPrice &&
    savingPercentage &&
    isDiscountLabelPresent &&
    !hasPerUnitPricing &&
    !hideSavings &&
    !showBestMarketValue
  )

  const showPerUnitSavingsPercentage = !!(
    perUnitSavingsPercentage &&
    !formattedFestivalOfferPrice &&
    !(formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE) &&
    !hideSavings &&
    !showBestMarketValue
  )
  const perUnitLabel = getUnitForPerServing(product)
  const unitForPerServing = perUnitLabel ? t(perUnitLabel) : ''
  const offerStyleKey =
    STYLE_KEY_MAP[
      getPriceStyleKey(
        !!formattedFestivalOfferPrice,
        !!formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE,
      )
    ]
  // Earn-credit pill (3P only): base catalog tags first (`additionalProductTags: []`), falling back
  // to the offer-scoped list so an earn tag carried only on the active offer still shows. Base wins
  // over an offer-carried earn tag DELIBERATELY: SVD resolves this pill from per-variant base tags
  // only, so offer-wins here would re-split the SVD/non-SVD buckets — the catalog tag is the source
  // of truth, and a credit boost must land there, not on offer display tags.
  const earnCreditTagFor3PVendor = isVariantThirdParty
    ? getEarnCreditTagFor3PVendor(
        svdVariantTags ??
          productHelper.getApplicableTags({
            product,
            selectedOptions: selectedOptions ?? [],
            productTags: product?.tags,
            additionalProductTags: [],
          }),
      ) ||
      getEarnCreditTagFor3PVendor(
        svdVariantTags ??
          productHelper.getApplicableTags({
            product,
            selectedOptions: selectedOptions ?? [],
            productTags: product?.tags,
          }),
      ) ||
      undefined
    : undefined

  // Offer-value selection requires a formatted subscription price...
  const isSubscriptionOffer = !!formattedSubscriptionPrice && purchaseOption === PURCHASE_TYPES.SUBSCRIBE
  // ...whereas the per-unit offer row uses this raw subscribe check WITHOUT requiring one, matching legacy.
  const isSubscribePurchase = purchaseOption === PURCHASE_TYPES.SUBSCRIBE

  return {
    isDiscountLabelPresent,
    hasPerUnitPricing,
    perUnitSavingsPercentage,
    showBestMarketValue,
    showSavingPercentage,
    showPerUnitSavingsPercentage,
    unitForPerServing,
    offerStyleKey,
    earnCreditTagFor3PVendor,
    isSubscriptionOffer,
    isSubscribePurchase,
  }
}
